/**
 * Shared axis machinery for the view templates: "nice round number" ticks (bar, distribution,
 * line, capacity; the field template's log-velocity axis reuses this for its VALUES, since the
 * log scale only changes where a tick's value is positioned, not which values are worth
 * showing), the pixel metrics every SVG template lays its axes out with, and the tick-thinning
 * that keeps labels from colliding.
 */

/**
 * Axis metrics in CSS PIXELS, per view size.
 *
 * Real pixels, not `viewBox` units: every SVG template now draws at its measured size (see
 * `useChartSize`), so type stays legible and constant while only the plot area responds to a
 * card being resized. `padL`/`padB` reserve room for a tick label AND its axis title — both
 * axes are titled at both sizes, since a tick value with no unit is not a label.
 */
export const AXIS = {
  card: { font: 10, padL: 50, padR: 14, padT: 10, padB: 40 },
  expanded: { font: 11, padL: 60, padR: 16, padT: 14, padB: 48 },
} as const;

/** Tick labels and axis titles — muted against the ink used for the axis lines themselves. */
export const AXIS_LABEL = "#5C6769";

/** Tick VALUES for a log axis: 1, 3, 10, 30, … up to `max`.
 *
 * A log axis must not be ticked with linear "nice numbers" — 1,000 / 2,000 / 3,000 / 4,000 land
 * in the last fifth of the axis and overprint each other while the whole low end goes
 * unlabelled. One-and-three per decade is the ladder the field's own interactive plot uses, so
 * a card and the full field read the same way. */
export function logTicks(max: number): number[] {
  if (!Number.isFinite(max) || max <= 0) {
    return [];
  }
  const ticks: number[] = [];
  for (let decade = 1; decade <= max; decade *= 10) {
    for (const step of [1, 3]) {
      const tick = decade * step;
      if (tick <= max) {
        ticks.push(tick);
      }
    }
  }
  return ticks;
}

/** Keeps the first tick, then each next one at least `minGap` pixels along, dropping the rest.
 *  A log axis bunches its high end (2,000 and 4,000 land ~20px apart on a card), and a linear
 *  axis does the same whenever the box is narrow; either way the labels overprint without
 *  this. `positionOf` returns a tick's pixel position along the axis. */
export function spacedTicks<T>(ticks: T[], positionOf: (tick: T) => number, minGap: number): T[] {
  const kept: T[] = [];
  let last = Number.NEGATIVE_INFINITY;
  for (const tick of ticks) {
    const at = positionOf(tick);
    if (at - last < minGap) {
      continue;
    }
    kept.push(tick);
    last = at;
  }
  return kept;
}

/** Round tick values from 0 up to (at least) `max`, spaced by a power-of-ten multiple of 1, 2
 *  or 5 — the standard "nice numbers" axis algorithm. `targetCount` is a hint, not a guarantee:
 *  a `max` of 0 or a non-finite value degrades to a single tick at 0 rather than throwing. */
export function niceTicks(max: number, targetCount = 5): number[] {
  if (!Number.isFinite(max) || max <= 0) {
    return [0];
  }
  const rawStep = max / Math.max(1, targetCount);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const niceNormalized = normalized >= 5 ? 10 : normalized >= 2 ? 5 : normalized >= 1 ? 2 : 1;
  const step = niceNormalized * magnitude;

  const ticks: number[] = [];
  for (let v = 0; v <= max + step * 0.5; v += step) {
    ticks.push(Math.round(v / step) * step);
  }
  return ticks;
}
